// Fase R1 — Canali personali della receptionist: WhatsApp (Embedded Signup /
// Coexistence) e Telefonia (Twilio Verified Caller ID). Nessun token/WABA in UI.
import { useCallback, useEffect, useState } from "react";
import { MessageCircle, Phone, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cbApiErr } from "@/lib/callbacks";

const WA_MODE_LABEL = { coexistence: "WhatsApp Business + Memento" };
const VOICE_LABEL = {
  not_configured: "Non configurato",
  verification_pending: "Verifica in corso",
  verified: "Verificato con Twilio",
  verification_failed: "Verifica non riuscita",
};

function Row({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1">
      <span className="label-eyebrow">{label}</span>
      <span className="font-bold text-sm text-right" data-testid={testid}>{value}</span>
    </div>
  );
}

export function ReadinessCard({ readiness }) {
  if (!readiness) return null;
  const items = [
    ["google_connected", "Google Calendar"],
    ["whatsapp_connected", "WhatsApp"],
    ["voice_verified", "Telefonia"],
    ["working_hours_configured", "Orari di lavoro"],
  ];
  return (
    <div className="sp-card p-5 mb-4" data-testid="rc-readiness-card">
      <div className="text-sm font-black mb-3">Configurazione operativa</div>
      <div className="grid grid-cols-2 gap-2">
        {items.map(([k, label]) => (
          <div key={k} data-testid={`rc-ready-${k}`} className="flex items-center gap-2 text-sm">
            {readiness[k]
              ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              : <AlertTriangle className="h-4 w-4 text-amber-500" />}
            <span className={readiness[k] ? "" : "text-[color:var(--sp-text-muted)]"}>
              {readiness[k] ? label : `${label} non collegato`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ReceptionistChannels({ onReadiness }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/agency/channels/me");
      setData(r.data);
      setPhone(r.data?.voice?.phone_number || "");
      onReadiness?.(r.data?.readiness);
    } catch (e) { setData(null); }
  }, [onReadiness]);

  useEffect(() => { load(); }, [load]);

  const connectWhatsApp = async () => {
    const cfg = data?.whatsapp_signup;
    if (!window.FB || !cfg?.configuration_id) {
      toast.error("Collegamento WhatsApp non disponibile: riprova tra poco.");
      return;
    }
    setBusy(true);
    window.FB.login(async (resp) => {
      const authCode = resp?.authResponse?.code;
      if (!authCode) { setBusy(false); toast.error("Collegamento annullato"); return; }
      try {
        await api.post("/agency/channels/me/whatsapp/connect", { code: authCode, mode: "coexistence" });
        toast.success("WhatsApp collegato");
        load();
      } catch (e) {
        const d = e?.response?.data?.detail;
        toast.error(d?.message || cbApiErr(e, "Collegamento non riuscito"));
      } finally { setBusy(false); }
    }, { config_id: cfg.configuration_id, response_type: "code", override_default_response_type: true });
  };

  const disconnectWhatsApp = async () => {
    if (!window.confirm("Disconnettere WhatsApp? Le conversazioni, i contatti e i casi restano salvati.")) return;
    setBusy(true);
    try {
      await api.post("/agency/channels/me/whatsapp/disconnect");
      toast.success("WhatsApp disconnesso");
      load();
    } catch (e) { toast.error(cbApiErr(e, "Errore")); }
    finally { setBusy(false); }
  };

  const saveVoice = async () => {
    setBusy(true);
    try {
      await api.put("/agency/channels/me/voice", { phone_number: phone });
      toast.success("Numero salvato");
      setCode(null);
      load();
    } catch (e) { toast.error(cbApiErr(e, "Numero non valido")); }
    finally { setBusy(false); }
  };

  const verifyVoice = async () => {
    setBusy(true);
    try {
      const r = await api.post("/agency/channels/me/voice/verify");
      setCode(r.data?.validation_code);
      toast.success("Chiamata di verifica in arrivo");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || cbApiErr(e, "Verifica non avviata"));
    } finally { setBusy(false); }
  };

  const reconcileVoice = async () => {
    setBusy(true);
    try {
      const r = await api.post("/agency/channels/me/voice/reconcile");
      const st = r.data?.voice?.caller_id_status;
      toast[st === "verified" ? "success" : "message"](VOICE_LABEL[st] || st);
      load();
    } catch (e) { toast.error(cbApiErr(e, "Stato non verificabile")); }
    finally { setBusy(false); }
  };

  const removeVoice = async () => {
    if (!window.confirm("Rimuovere il numero per le chiamate?")) return;
    setBusy(true);
    try {
      await api.delete("/agency/channels/me/voice");
      setPhone(""); setCode(null);
      load();
    } catch (e) { toast.error(cbApiErr(e, "Errore")); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const wa = data.whatsapp || {};
  const voice = data.voice || {};

  return (
    <>
      {/* 4. WhatsApp ----------------------------------------------------- */}
      <div className="sp-card p-5 mb-4" data-testid="rc-whatsapp-section">
        <div className="text-sm font-black flex items-center gap-2 mb-3">
          <MessageCircle className="h-4 w-4 text-[color:var(--sp-brand)]" /> WhatsApp
        </div>
        {wa.connected ? (
          <>
            <Row label="Numero" value={wa.display_phone_number || "—"} testid="rc-wa-number" />
            <Row label="Stato" value="✓ Collegato" testid="rc-wa-status" />
            <Row label="Modalità"
              value={WA_MODE_LABEL[wa.connection_mode] || wa.connection_mode || "—"}
              testid="rc-wa-mode" />
            <div className="flex gap-2 mt-3 flex-wrap">
              <button onClick={connectWhatsApp} disabled={busy} data-testid="rc-wa-manage"
                className="px-4 py-2 rounded-full border border-[color:var(--sp-border)] text-sm font-bold hover:bg-[color:var(--sp-subtle)] disabled:opacity-50">
                Gestisci
              </button>
              <button onClick={disconnectWhatsApp} disabled={busy} data-testid="rc-wa-disconnect"
                className="px-4 py-2 rounded-full border border-red-400 text-red-600 text-sm font-bold hover:bg-red-50 disabled:opacity-50">
                Disconnetti
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-[color:var(--sp-text-muted)]">
              Collega il tuo numero WhatsApp Business: potrai continuare a usare l'app
              WhatsApp Business sullo stesso numero.
            </p>
            {wa.last_error_class && (
              <p className="text-xs text-amber-700 mt-2" data-testid="rc-wa-error">
                Ultimo tentativo non riuscito ({wa.last_error_class}).
              </p>
            )}
            <button onClick={connectWhatsApp} disabled={busy} data-testid="rc-wa-connect"
              className="sp-btn-primary inline-flex items-center gap-2 mt-3 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
              Collega WhatsApp
            </button>
          </>
        )}
      </div>

      {/* 5. Telefonia ---------------------------------------------------- */}
      <div className="sp-card p-5 mb-4" data-testid="rc-voice-section">
        <div className="text-sm font-black flex items-center gap-2 mb-3">
          <Phone className="h-4 w-4 text-[color:var(--sp-brand)]" /> Telefonia
        </div>
        <label className="text-sm font-bold">Numero chiamate</label>
        <input className="sp-input mt-1" data-testid="rc-voice-phone" placeholder="+39 333 1234567"
          value={phone} onChange={(e) => setPhone(e.target.value)} />
        <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">
          Può coincidere con il numero WhatsApp, ma la verifica Twilio è un processo separato.
        </p>
        <Row label="Stato"
          value={(voice.caller_id_status === "verified" ? "✓ " : "") +
            (VOICE_LABEL[voice.caller_id_status] || voice.caller_id_status)}
          testid="rc-voice-status" />
        {code && (
          <div className="rounded-lg bg-[color:var(--sp-subtle)] px-3 py-2 text-sm mt-2" data-testid="rc-voice-code">
            Codice da digitare durante la chiamata: <b>{code}</b>
          </div>
        )}
        {voice.last_error && (
          <p className="text-xs text-amber-700 mt-2" data-testid="rc-voice-error">{voice.last_error}</p>
        )}
        <div className="flex gap-2 mt-3 flex-wrap">
          <button onClick={saveVoice} disabled={busy || !phone} data-testid="rc-voice-save"
            className="sp-btn-primary disabled:opacity-50">Salva numero</button>
          <button onClick={verifyVoice} disabled={busy || !voice.phone_number} data-testid="rc-voice-verify"
            className="px-4 py-2 rounded-full border border-[color:var(--sp-border)] text-sm font-bold hover:bg-[color:var(--sp-subtle)] disabled:opacity-50">
            Verifica con Twilio
          </button>
          <button onClick={reconcileVoice} disabled={busy || !voice.phone_number} data-testid="rc-voice-reconcile"
            className="px-4 py-2 rounded-full border border-[color:var(--sp-border)] text-sm font-bold hover:bg-[color:var(--sp-subtle)] disabled:opacity-50">
            Aggiorna stato
          </button>
          {voice.phone_number && (
            <button onClick={removeVoice} disabled={busy} data-testid="rc-voice-remove"
              className="px-4 py-2 rounded-full border border-red-400 text-red-600 text-sm font-bold hover:bg-red-50 disabled:opacity-50">
              Rimuovi
            </button>
          )}
        </div>
        {!data.voice_provider_configured && (
          <p className="text-xs text-amber-700 mt-2" data-testid="rc-voice-provider-warning">
            Provider telefonia non configurato sul server.
          </p>
        )}
      </div>
    </>
  );
}

// Vista SOLA LETTURA per agency_admin (nessun OAuth al posto della receptionist).
const TPL_STATUS_LABEL = {
  approved: "Approvato",
  pending: "In revisione",
  rejected: "Rifiutato",
  missing: "Non presente su questo numero",
  not_configured: "Template non ancora indicato",
};

export function AdminChannelsView({ receptionistId }) {
  const [d, setD] = useState(null);
  const [tpl, setTpl] = useState(null);
  useEffect(() => {
    if (!receptionistId) { setD(null); setTpl(null); return; }
    api.get(`/agency/channels/receptionists/${receptionistId}`)
      .then((r) => setD(r.data)).catch(() => setD(null));
    api.get(`/agency/whatsapp/template-requirements?receptionist_user_id=${receptionistId}`)
      .then((r) => setTpl((r.data?.owners || [])[0] || null)).catch(() => setTpl(null));
  }, [receptionistId]);
  if (!d) return null;
  return (
    <div className="sp-card p-5 mb-4" data-testid="rc-admin-view">
      <div className="text-sm font-black mb-3">Canali della receptionist (sola lettura)</div>
      <Row label="Google Calendar"
        value={d.google_calendar?.connected ? `✓ ${d.google_calendar.google_account_email || "Collegato"}` : "Non collegato"}
        testid="rc-admin-google" />
      <Row label="WhatsApp"
        value={d.whatsapp?.connected ? `✓ ${d.whatsapp.display_phone_number || "Collegato"}` : "Non collegato"}
        testid="rc-admin-whatsapp" />
      <Row label="Telefonia"
        value={d.voice?.caller_id_status === "verified"
          ? `✓ ${d.voice.phone_number} verificato`
          : (VOICE_LABEL[d.voice?.caller_id_status] || "Non configurato")}
        testid="rc-admin-voice" />
      <Row label="Orari di lavoro" value={d.working_hours_configured ? "Configurati" : "Non configurati"}
        testid="rc-admin-hours" />

      {tpl && (
        <div className="mt-4 pt-3 border-t border-[color:var(--sp-border)]" data-testid="rc-admin-templates">
          <div className="text-sm font-black mb-1">Template WhatsApp richiesti</div>
          <p className="text-xs text-[color:var(--sp-text-muted)] mb-2">
            Fuori dalle 24 ore dall'ultimo messaggio del cliente servono template approvati
            sul numero WhatsApp di questa receptionist.
          </p>
          {tpl.items.map((it) => (
            <div key={it.key} data-testid={`rc-tpl-${it.key}`}
              className="flex items-start justify-between gap-3 py-1 text-sm">
              <div>
                <div className="font-bold">{it.label}</div>
                <div className="text-xs text-[color:var(--sp-text-muted)]">
                  {it.template_name ? `Template: ${it.template_name}` : "Nessun template indicato"}
                  {it.language ? ` · ${it.language}` : ""}
                </div>
              </div>
              <span className={`text-xs font-bold whitespace-nowrap ${it.status === "approved" ? "text-emerald-600" : "text-amber-700"}`}
                data-testid={`rc-tpl-status-${it.key}`}>
                {TPL_STATUS_LABEL[it.status] || it.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
